import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import type { AuthStatus } from "../api/types";

export type AuthState =
  | { kind: "loading" }
  | { kind: "open" } // password not configured server-side
  | { kind: "needs_login" }
  | { kind: "authenticated" };

export function useAuth() {
  const [state, setState] = useState<AuthState>({ kind: "loading" });

  const refresh = useCallback(async () => {
    try {
      const status = await api.get<AuthStatus>("/api/auth-status");
      if (!status.required) setState({ kind: "open" });
      else if (status.authenticated) setState({ kind: "authenticated" });
      else setState({ kind: "needs_login" });
    } catch {
      // If the auth-status call itself fails we treat it as needing login —
      // that's the safe default and matches the legacy app's behavior.
      setState({ kind: "needs_login" });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (password: string): Promise<{ ok: true } | { ok: false; error: string }> => {
      try {
        await api.post<{ ok: true }>("/api/login", { password });
        setState({ kind: "authenticated" });
        return { ok: true };
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Login fehlgeschlagen";
        return { ok: false, error: message };
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.post<{ ok: true }>("/api/logout");
    } finally {
      await refresh();
    }
  }, [refresh]);

  return { state, login, logout, refresh };
}
